import { useEffect, useRef, useState } from 'react'
import type { DataSnapshot } from '../api/types'

// VersionPicker is the pill-shaped trigger + dropdown panel that the
// Data tab uses to time-travel its file listing. Pure presentation —
// the parent owns the snapshot list and the current selection, and
// supplies an onChange callback that flips selectedBucketAt on click.
//
// Selection model:
//   - selectedBucketAt === null → "Live" (current state). Dot is green,
//     trigger label says "Live".
//   - selectedBucketAt !== null → "viewing a historical snapshot". Dot
//     is orange, trigger label is the bucket's HH:MM timestamp.
//
// Design tokens map to the existing Tailwind palette + the `glow-*`
// brand colors already defined in tailwind.config.js (the design's
// `#fff7ed` selected-row background IS `glow-50`).
export interface VersionPickerProps {
  snapshots: DataSnapshot[]
  selectedBucketAt: string | null
  onChange: (bucketAt: string | null) => void
  className?: string
}

export function VersionPicker(props: VersionPickerProps) {
  const { snapshots, selectedBucketAt, onChange, className = '' } = props
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  // Click-outside + Esc-to-close. Both standard hand-rolled patterns
  // since the SPA has no headless dropdown primitive yet.
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // The "live" snapshot is whatever is on the snapshots list newest-first.
  // The parent passes null for selectedBucketAt to mean "live"; we render
  // the trigger accordingly.
  const isLive = selectedBucketAt === null
  const triggerLabel = isLive
    ? 'Live'
    : selectedBucketAt
      ? shortTime(selectedBucketAt)
      : 'Live'

  return (
    <div ref={rootRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={[
          'inline-flex items-center gap-2 rounded-lg border border-line px-3 py-1.5',
          'text-[12.5px] text-cream',
          'hover:bg-surface-2',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-glow-500/40',
          open ? 'bg-surface-2' : 'bg-surface',
        ].join(' ')}
      >
        <span
          aria-hidden="true"
          className={[
            'h-1.5 w-1.5 rounded-full',
            isLive ? 'bg-emerald-500' : 'bg-glow-500',
          ].join(' ')}
        />
        <span className="font-mono text-[12px]">{triggerLabel}</span>
        <ChevronDown />
      </button>

      {open && (
        <div
          role="menu"
          className={[
            'absolute right-0 z-10 mt-1.5 min-w-[320px] overflow-hidden',
            'rounded-[10px] border border-line bg-surface',
            'shadow-[0_12px_28px_rgba(15,23,42,0.12),0_2px_4px_rgba(15,23,42,0.04)]',
          ].join(' ')}
        >
          <div className="border-b border-line px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted">
            Recent versions
          </div>

          {/* Live row first so the user always has a one-click "back to
              the present" affordance even when no historical writes exist. */}
          <PickerRow
            isLive
            isSelected={isLive}
            label="Live"
            timestamp=""
            delta={null}
            onPick={() => {
              onChange(null)
              setOpen(false)
            }}
          />

          {snapshots.map((s) => (
            <PickerRow
              key={s.bucket_at}
              isLive={false}
              isSelected={selectedBucketAt === s.bucket_at}
              label={`g${s.generation_high}`}
              timestamp={shortTime(s.bucket_at)}
              delta={s.delta}
              onPick={() => {
                onChange(s.bucket_at)
                setOpen(false)
              }}
            />
          ))}

          {snapshots.length === 0 && (
            <div className="border-t border-line px-3 py-3 text-[12px] text-muted">
              No prior versions yet. Each write to{' '}
              <code className="font-mono text-[11px]">window.backlit.data</code>{' '}
              adds a snapshot here.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface PickerRowProps {
  isLive: boolean
  isSelected: boolean
  label: string
  timestamp: string
  delta: { added: number; modified: number; removed: number } | null
  onPick: () => void
}

function PickerRow(props: PickerRowProps) {
  const { isLive, isSelected, label, timestamp, delta, onPick } = props
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={isSelected}
      onClick={onPick}
      className={[
        'flex w-full items-center gap-2.5 border-t border-line px-3 py-2.5 text-left',
        'first:border-t-0 hover:bg-surface-2',
        isSelected ? 'bg-glow-50 hover:bg-glow-50' : 'bg-surface',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-glow-500/40',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'h-1.5 w-1.5 flex-shrink-0 rounded-full',
          isLive
            ? 'bg-emerald-500'
            : isSelected
              ? 'bg-glow-500'
              : 'bg-surface-2',
        ].join(' ')}
      />
      <span className="flex-1 truncate font-mono text-[12px] text-cream">
        {label}
      </span>
      {isLive && (
        <span className="inline-flex items-center rounded-full border border-emerald-500/25 bg-emerald-500/15 px-1.5 py-px text-[11px] font-medium text-emerald-300">
          Live
        </span>
      )}
      {timestamp && (
        <span className="text-[11.5px] tabular-nums text-muted">
          {timestamp}
        </span>
      )}
      <span className="w-14 text-right font-mono text-[10.5px]">
        {delta && delta.added > 0 && (
          <span className="text-emerald-300">+{delta.added} </span>
        )}
        {delta && delta.modified > 0 && (
          <span className="text-yellow-300">~{delta.modified} </span>
        )}
        {delta && delta.removed > 0 && (
          <span className="text-red-300">-{delta.removed}</span>
        )}
      </span>
    </button>
  )
}

function ChevronDown() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      className="ml-0.5 text-muted"
      aria-hidden="true"
    >
      <path
        d="M2 3.5L5 6.5L8 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// shortTime renders an ISO/RFC 3339 timestamp as HH:MM. Best-effort —
// invalid input returns the raw string so a server quirk is at least
// visible to the user.
function shortTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
  } catch {
    return iso
  }
}
